import type { SearchCredentials } from '@shared/core/search';

import { SearchAuthError } from '../transport/types';

/**
 * Credentials whose re-mint has settled, and the credentials such a re-mint produced. Neither is
 * re-minted again: a refused key costs one re-mint per scheduled credentials refresh, not one per
 * keystroke.
 */
const reminted = new WeakSet<SearchCredentials>();

/**
 * The credentials each settled re-mint produced. A refusal of the old key that lands after its
 * re-mint settled (a second request already in flight on it) resends once on these.
 */
const successorOf = new WeakMap<SearchCredentials, SearchCredentials>();

/** The re-mint a refusal started for a credentials object, while it is in flight; concurrent refusals share it. */
const remintInFlight = new WeakMap<SearchCredentials, Promise<SearchCredentials | null>>();

/** Re-mints once per refused credentials object; null credentials (the caller cannot tell) are never shared. */
const remintFor = (
  credentials: SearchCredentials | null,
  remint: () => Promise<SearchCredentials | null>
): Promise<SearchCredentials | null> => {
  const inFlight = credentials === null ? undefined : remintInFlight.get(credentials);
  if (inFlight !== undefined) return inFlight;

  const started = (async () => {
    let fresh: SearchCredentials | null = null;
    try {
      fresh = await remint();
      if (fresh !== null) reminted.add(fresh);
      return fresh;
    } finally {
      if (credentials !== null) {
        remintInFlight.delete(credentials);
        reminted.add(credentials);
        if (fresh !== null) successorOf.set(credentials, fresh);
      }
    }
  })();
  if (credentials !== null) remintInFlight.set(credentials, started);
  return started;
};

export interface RetryOnRefusedKeyArgs<T> {
  /** The credentials `send` searches on; null when the caller cannot tell which they are. */
  readonly credentials: SearchCredentials | null;
  readonly send: () => Promise<T>;
  /** Mints fresh credentials; null when the provider has none to give. */
  readonly remint: () => Promise<SearchCredentials | null>;
  /** The same search on the fresh credentials, or null when it cannot run on them. */
  readonly resend: (fresh: SearchCredentials) => Promise<T> | null;
  /** False once nobody waits for the answer; nothing is re-minted or resent then. */
  readonly isCurrent: () => boolean;
}

/**
 * Sends a search and, when the engine refuses the key, re-mints once and resends on the fresh
 * credentials; a refusal of a key whose re-mint already settled resends on its successor instead.
 * Any other failure, and a refusal that is not retried, rejects with the original error.
 */
export async function retryOnRefusedKey<T>({
  credentials,
  send,
  remint,
  resend,
  isCurrent,
}: RetryOnRefusedKeyArgs<T>): Promise<T> {
  try {
    return await send();
  } catch (error) {
    if (!(error instanceof SearchAuthError) || !isCurrent()) throw error;
    const settled = credentials !== null && reminted.has(credentials);
    const fresh = settled ? (successorOf.get(credentials) ?? null) : await remintFor(credentials, remint);
    const resent = fresh === null || !isCurrent() ? null : resend(fresh);
    if (resent === null) throw error;
    return resent;
  }
}
